import { LineChartSeries } from "components/charts/LineChart";
import {
  CustomerHealth,
  CustomerHealthResponse,
  CustomerHealthStatus,
  EndpointStats,
  EngagementFunnelResponse,
  SecurityViolationStatsResponse,
  SessionQualityResponse,
  SkillUsageStats,
} from "services/dashboardService";

// Generates the last `n` calendar dates (ending today) formatted in the user's locale
function generateLastNDates(n: number): string[] {
  const today = new Date();
  const dates: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    dates.push(
      d.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
    );
  }
  return dates;
}

// 14-day message counts per status — indices map to [today-13 … today]
const MESSAGES_COUNTS: { Success: number; Error: number; Pending: number }[] = [
  { Success: 312, Error: 18, Pending: 24 },
  { Success: 284, Error: 21, Pending: 19 },
  { Success: 401, Error: 14, Pending: 31 },
  { Success: 356, Error: 22, Pending: 28 },
  { Success: 498, Error: 17, Pending: 35 },
  { Success: 521, Error: 19, Pending: 42 },
  { Success: 389, Error: 23, Pending: 27 },
  { Success: 442, Error: 15, Pending: 33 },
  { Success: 467, Error: 20, Pending: 38 },
  { Success: 503, Error: 16, Pending: 41 },
  { Success: 587, Error: 12, Pending: 45 },
  { Success: 612, Error: 14, Pending: 48 },
  { Success: 574, Error: 18, Pending: 43 },
  { Success: 635, Error: 11, Pending: 50 },
];

// Tool executions: 3 tools over 14 days
const TOOL_EXECUTION_DATA: { name: string; counts: number[] }[] = [
  {
    name: "search",
    counts: [45, 52, 61, 58, 74, 82, 67, 78, 83, 91, 98, 105, 99, 112],
  },
  {
    name: "create-ticket",
    counts: [23, 27, 31, 29, 38, 42, 34, 39, 41, 44, 52, 56, 51, 58],
  },
  {
    name: "send-email",
    counts: [18, 21, 24, 22, 29, 33, 27, 31, 34, 36, 41, 45, 42, 47],
  },
];

// Daily active users over 14 days
const DAU_COUNTS: number[] = [
  87, 79, 104, 95, 128, 143, 112, 119, 131, 148, 162, 177, 159, 184,
];

// 14-day feedback counts — indices map to [today-13 … today]
const FEEDBACK_COUNTS: { Positive: number; Negative: number }[] = [
  { Positive: 48, Negative: 7 },
  { Positive: 41, Negative: 9 },
  { Positive: 63, Negative: 5 },
  { Positive: 55, Negative: 8 },
  { Positive: 72, Negative: 6 },
  { Positive: 81, Negative: 7 },
  { Positive: 58, Negative: 10 },
  { Positive: 67, Negative: 6 },
  { Positive: 74, Negative: 8 },
  { Positive: 82, Negative: 5 },
  { Positive: 91, Negative: 4 },
  { Positive: 96, Negative: 6 },
  { Positive: 88, Negative: 7 },
  { Positive: 103, Negative: 4 },
];

// Security violations over 14 days
const SECURITY_VIOLATION_COUNTS: number[] = [
  2, 1, 3, 0, 2, 1, 4, 1, 0, 2, 1, 3, 2, 1,
];

// Top endpoints (static aggregate — not time-sliced)
const MOCK_TOP_ENDPOINTS: EndpointStats[] = [
  {
    toolName: "search",
    endpoint: "/api/search",
    successCount: 1042,
    errorCount: 38,
    totalCount: 1080,
  },
  {
    toolName: "create-ticket",
    endpoint: "/api/tickets",
    successCount: 718,
    errorCount: 22,
    totalCount: 740,
  },
  {
    toolName: "send-email",
    endpoint: "/api/email/send",
    successCount: 581,
    errorCount: 14,
    totalCount: 595,
  },
  {
    toolName: "get-customer",
    endpoint: "/api/customers/{id}",
    successCount: 492,
    errorCount: 11,
    totalCount: 503,
  },
  {
    toolName: "update-record",
    endpoint: "/api/records/{id}",
    successCount: 374,
    errorCount: 19,
    totalCount: 393,
  },
  {
    toolName: "list-orders",
    endpoint: "/api/orders",
    successCount: 312,
    errorCount: 8,
    totalCount: 320,
  },
  {
    toolName: "get-product",
    endpoint: "/api/products/{id}",
    successCount: 261,
    errorCount: 6,
    totalCount: 267,
  },
  {
    toolName: "create-case",
    endpoint: "/api/cases",
    successCount: 198,
    errorCount: 13,
    totalCount: 211,
  },
];

// Top skills by usage (static aggregate — distinct turns per skill)
const MOCK_TOP_SKILLS: SkillUsageStats[] = [
  {
    skillId: "skill-1",
    skillName: "Refund Handler",
    source: "tenant",
    usageCount: 412,
  },
  {
    skillId: "skill-2",
    skillName: "Order Lookup",
    source: "tenant",
    usageCount: 357,
  },
  {
    skillId: "cc-skill-3",
    skillName: "Tone Policy",
    source: "cc",
    usageCount: 289,
  },
  {
    skillId: "skill-4",
    skillName: "Shipping Estimator",
    source: "tenant",
    usageCount: 214,
  },
  {
    skillId: "cc-skill-5",
    skillName: "Escalation Rules",
    source: "cc",
    usageCount: 173,
  },
  {
    skillId: "skill-6",
    skillName: "Product Recommender",
    source: "tenant",
    usageCount: 128,
  },
  {
    skillId: "skill-7",
    skillName: "Account Verification",
    source: "tenant",
    usageCount: 96,
  },
];

// Negative feedback reasons (static)
const MOCK_NEGATIVE_FEEDBACK: { reason: string; count: number }[] = [
  { reason: "wrong-answer", count: 34 },
  { reason: "too-slow", count: 28 },
  { reason: "incomplete-response", count: 21 },
  { reason: "misunderstood-intent", count: 17 },
  { reason: "formatting-issue", count: 12 },
  { reason: "other", count: 8 },
];

// Engagement funnel (static)
const MOCK_ENGAGEMENT_FUNNEL: EngagementFunnelResponse = {
  stages: [
    {
      stageName: "Widget Loads",
      count: 8420,
      conversionRate: 100,
      overallConversionRate: 100,
    },
    {
      stageName: "Sessions Started",
      count: 5813,
      conversionRate: 69.0,
      overallConversionRate: 69.0,
    },
    {
      stageName: "Messages Sent",
      count: 4241,
      conversionRate: 72.9,
      overallConversionRate: 50.4,
    },
    {
      stageName: "Tools Executed",
      count: 2874,
      conversionRate: 67.8,
      overallConversionRate: 34.1,
    },
  ],
};

// Session quality (static)
const MOCK_SESSION_QUALITY: SessionQualityResponse = {
  quality: {
    averageSessionDurationMinutes: 4.2,
    averageMessagesPerSession: 6.5,
    bounceRate: 17.8,
    resolutionRate: 74.3,
    averageMessagesToFirstTool: 2.1,
    totalSessions: 5813,
    activeSessions: 142,
  },
};

// Customer health (static — 10 customers)
const MOCK_CUSTOMERS: CustomerHealth[] = [
  {
    customerExternalId: "acme-corp",
    customerName: "Acme Corp",
    customerEmail: "ops@acme.example",
    healthScore: 92,
    status: CustomerHealthStatus.Healthy,
    lastActivityAt: "2026-03-28T14:22:00Z",
    daysSinceLastActivity: 0,
    successRate: 96.2,
    totalSessions: 312,
    totalMessages: 2034,
    violationCount: 0,
    authFailureCount: 1,
    riskFactors: [],
  },
  {
    customerExternalId: "globotech",
    customerEmail: "admin@globotech.example",
    healthScore: 88,
    status: CustomerHealthStatus.Healthy,
    lastActivityAt: "2026-03-28T11:05:00Z",
    daysSinceLastActivity: 0,
    successRate: 94.1,
    totalSessions: 278,
    totalMessages: 1812,
    violationCount: 1,
    authFailureCount: 2,
    riskFactors: [],
  },
  {
    customerExternalId: "nimbus-inc",
    healthScore: 81,
    status: CustomerHealthStatus.Healthy,
    lastActivityAt: "2026-03-27T18:44:00Z",
    daysSinceLastActivity: 1,
    successRate: 91.7,
    totalSessions: 204,
    totalMessages: 1345,
    violationCount: 2,
    authFailureCount: 3,
    riskFactors: [],
  },
  {
    customerExternalId: "delta-systems",
    healthScore: 74,
    status: CustomerHealthStatus.Healthy,
    lastActivityAt: "2026-03-26T09:30:00Z",
    daysSinceLastActivity: 2,
    successRate: 88.5,
    totalSessions: 167,
    totalMessages: 1092,
    violationCount: 3,
    authFailureCount: 4,
    riskFactors: [],
  },
  {
    customerExternalId: "peak-solutions",
    healthScore: 62,
    status: CustomerHealthStatus.AtRisk,
    lastActivityAt: "2026-03-24T16:12:00Z",
    daysSinceLastActivity: 4,
    successRate: 78.3,
    totalSessions: 98,
    totalMessages: 641,
    violationCount: 5,
    authFailureCount: 8,
    riskFactors: ["declining-session-count", "elevated-error-rate"],
  },
  {
    customerExternalId: "redrock-enterprises",
    healthScore: 76,
    status: CustomerHealthStatus.Healthy,
    lastActivityAt: "2026-03-22T14:00:00Z",
    daysSinceLastActivity: 6,
    successRate: 86.9,
    totalSessions: 82,
    totalMessages: 534,
    violationCount: 2,
    authFailureCount: 4,
    riskFactors: [],
  },
  {
    customerExternalId: "clearway-ltd",
    healthScore: 73,
    status: CustomerHealthStatus.Healthy,
    lastActivityAt: "2026-03-20T10:55:00Z",
    daysSinceLastActivity: 8,
    successRate: 84.6,
    totalSessions: 63,
    totalMessages: 412,
    violationCount: 2,
    authFailureCount: 3,
    riskFactors: [],
  },
  {
    customerExternalId: "stellar-group",
    healthScore: 38,
    status: CustomerHealthStatus.Churning,
    lastActivityAt: "2026-03-14T08:20:00Z",
    daysSinceLastActivity: 14,
    successRate: 61.4,
    totalSessions: 34,
    totalMessages: 218,
    violationCount: 12,
    authFailureCount: 19,
    riskFactors: ["long-inactivity", "low-engagement", "security-violations"],
  },
  {
    customerExternalId: "horizon-tech",
    healthScore: 71,
    status: CustomerHealthStatus.Healthy,
    lastActivityAt: "2026-03-10T07:15:00Z",
    daysSinceLastActivity: 18,
    successRate: 82.4,
    totalSessions: 21,
    totalMessages: 134,
    violationCount: 1,
    authFailureCount: 2,
    riskFactors: [],
  },
  {
    customerExternalId: "nova-dynamics",
    healthScore: 69,
    status: CustomerHealthStatus.Healthy,
    lastActivityAt: "2026-03-05T06:00:00Z",
    daysSinceLastActivity: 23,
    successRate: 80.8,
    totalSessions: 11,
    totalMessages: 67,
    violationCount: 1,
    authFailureCount: 2,
    riskFactors: [],
  },
];

const MOCK_CUSTOMER_HEALTH: CustomerHealthResponse = {
  customers: [...MOCK_CUSTOMERS].sort(
    (left, right) => right.healthScore - left.healthScore,
  ),
  healthyCount: 8,
  atRiskCount: 1,
  churningCount: 1,
};

// ─── Public API ──────────────────────────────────────────────────────────────

export interface MockDashboardData {
  messagesData: any[];
  toolExecutionsSeries: LineChartSeries[];
  dailyActiveUsers: LineChartSeries[];
  topEndpoints: EndpointStats[];
  topSkills: SkillUsageStats[];
  feedbackTrend: any[];
  negativeFeedbackReasons: any[];
  securityViolations: SecurityViolationStatsResponse;
  engagementFunnel: EngagementFunnelResponse;
  sessionQuality: SessionQualityResponse;
  customerHealth: CustomerHealthResponse;
}

/**
 * Returns mock dashboard data for demo mode.
 * Time-series data is sliced to the last `timeRange` days of the 14-day base.
 * Aggregate data (endpoints, funnel, quality, health) is always the full set.
 */
export function getMockDashboardData(timeRange: number): MockDashboardData {
  const days = Math.min(timeRange, 14);
  const dates = generateLastNDates(days);
  const offset = 14 - days; // offset into the 14-element arrays

  // Messages bar chart
  const messagesData = MESSAGES_COUNTS.slice(offset).map((counts, i) => ({
    time: dates[i],
    ...counts,
  }));

  // Tool executions line chart
  const toolExecutionsSeries: LineChartSeries[] = TOOL_EXECUTION_DATA.map(
    (tool) => ({
      id: tool.name,
      label: tool.name,
      data: dates.map((date, i) => ({
        x: date,
        y: tool.counts[offset + i],
      })),
    }),
  );

  // Daily active users line chart
  const dailyActiveUsers: LineChartSeries[] = [
    {
      id: "users",
      label: "Active Users",
      data: dates.map((date, i) => ({
        x: date,
        y: DAU_COUNTS[offset + i],
      })),
    },
  ];

  // Feedback trend
  const feedbackTrend = FEEDBACK_COUNTS.slice(offset).map((counts, i) => ({
    date: dates[i],
    ...counts,
  }));

  // Security violations
  const violationSlice = SECURITY_VIOLATION_COUNTS.slice(offset);
  const totalViolations = violationSlice.reduce((a, b) => a + b, 0);
  const securityViolations: SecurityViolationStatsResponse = {
    violationsByTime: dates.map((date, i) => ({
      timestamp: date,
      violationCount: violationSlice[i],
    })),
    totalViolations,
    averageViolationsPerDay: parseFloat((totalViolations / days).toFixed(1)),
  };

  return {
    messagesData,
    toolExecutionsSeries,
    dailyActiveUsers,
    topEndpoints: MOCK_TOP_ENDPOINTS,
    topSkills: MOCK_TOP_SKILLS,
    feedbackTrend,
    negativeFeedbackReasons: MOCK_NEGATIVE_FEEDBACK,
    securityViolations,
    engagementFunnel: MOCK_ENGAGEMENT_FUNNEL,
    sessionQuality: MOCK_SESSION_QUALITY,
    customerHealth: MOCK_CUSTOMER_HEALTH,
  };
}
